import { useCallback, useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'
import { styled, Box, TextField, Slider, InputAdornment, useTheme } from '@mui/material'
import { Connection } from 'features/connections'
import { Typography } from '@carto/meridian-ds/components'
import settings from 'settings'

const connectionLimits = settings.connectionLimits

interface QueryTimeoutFormProps {
  form: Omit<UseFormReturn, 'handleSubmit' | 'formState'>
  connection?: Connection
}

const SliderTextField = styled(TextField)(({ theme }) => ({
  flex: `0 0 ${theme.spacing(10)}`,
  marginLeft: theme.spacing(1)
}))

const queryTimeoutSName = 'queryTimeoutS'
const defaultQueryTimeoutS = connectionLimits.queryTimeoutS.default
const { min: minQueryTimeoutS, max: maxQueryTimeoutS } = connectionLimits.queryTimeoutS

function QueryTimeoutForm({ form, connection }: QueryTimeoutFormProps) {
  const theme = useTheme()
  const { register, trigger, setValue } = form
  const [queryTimeoutS, _setQueryTimeoutS] = useState<number>(connection?.queryTimeoutS || defaultQueryTimeoutS)

  const setQueryTimeoutS = useCallback(
    (value) => {
      _setQueryTimeoutS(value)
      setValue(queryTimeoutSName, value, { shouldDirty: true })
      trigger(queryTimeoutSName)
    },
    [setValue, _setQueryTimeoutS, trigger]
  )

  const handleSliderChange = (e, newValue) => {
    setQueryTimeoutS(newValue)
  }

  const handleSliderTextChange = (e) => {
    let numericValue = Number(e.currentTarget.value)
    if (!Number.isInteger(numericValue)) {
      numericValue = queryTimeoutS
    } else {
      const { min, max } = connectionLimits.queryTimeoutS
      if (numericValue < min) {
        numericValue = min
      }
      if (numericValue > max) {
        numericValue = max
      }
    }
    setQueryTimeoutS(numericValue)
  }

  return (
    <Box width="100%" mt={-1} ml={3}>
      <Typography variant="caption" fontWeight={500} id={`query-control-slide-label`}>
        <FormattedMessage id="connections.create.advanced.queryConcurrencyControl.queryTimeoutS.label" />
      </Typography>
      <Box display="flex" alignItems="center">
        <Slider
          min={minQueryTimeoutS}
          max={maxQueryTimeoutS}
          name={`${queryTimeoutSName}-slider`}
          value={queryTimeoutS}
          onChange={handleSliderChange}
        />
        <SliderTextField
          fullWidth={true}
          size="small"
          defaultValue={queryTimeoutS}
          InputProps={{
            'aria-label': 'input-number',
            endAdornment: (
              <InputAdornment position="end" sx={{ fontSize: theme.typography.button.fontSize }}>
                <FormattedMessage id="connections.create.advanced.queryConcurrencyControl.queryTimeoutS.units" />
              </InputAdornment>
            ),
            inputProps: {
              min: minQueryTimeoutS,
              max: maxQueryTimeoutS,
              ...register(queryTimeoutSName, { required: false })
            }
          }}
          onChange={handleSliderTextChange}
        />
      </Box>
    </Box>
  )
}

export default QueryTimeoutForm
